import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'
import SEO from '@/components/university/common/SEO'

const M = '#06060c'
const G = '#d4a843'
const EM = '#10b981'
const BR = 'rgba(255,255,255,0.07)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'

type Stage = 'loading' | 'needs_account' | 'confirming' | 'activating' | 'success' | 'error'

interface ActivationResult {
    plan_key: string
    duration_months: number
    expires_at: string
    message: string
    already_had_access?: boolean
}

export default function ActivatePage() {
    const { code } = useParams<{ code: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const supabase = createClientComponentClient()
    const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl

    const [stage, setStage] = useState<Stage>('loading')
    const [result, setResult] = useState<ActivationResult | null>(null)
    const [errorMsg, setErrorMsg] = useState('')
    const [cleanCode, setCleanCode] = useState('')

    useEffect(() => {
        const c = (code || '').trim().toUpperCase()
        setCleanCode(c)

        if (!c) {
            setErrorMsg('No activation code provided.')
            setStage('error')
            return
        }

        if (!user) {
            sessionStorage.setItem('pending_activation_code', c)
            setStage('needs_account')
            return
        }

        setStage('confirming')
    }, [code, user])

    // If user just signed up/in with a pending code
    useEffect(() => {
        if (!user) return
        const pending = sessionStorage.getItem('pending_activation_code')
        if (pending && stage === 'loading') {
            setCleanCode(pending)
            setStage('confirming')
        }
    }, [user])

    async function activate() {
        if (!user || !cleanCode) return
        setStage('activating')

        try {
            const { data: { session } } = await supabase.auth.getSession()
            const token = session?.access_token

            const res = await fetch(
                `${supabaseUrl}/functions/v1/redeem-scholar-pass`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ code: cleanCode }),
                }
            )

            const data = await res.json()

            if (!res.ok || !data.success) {
                setErrorMsg(data.message || data.error || 'Activation failed. Please try again.')
                setStage('error')
                return
            }

            sessionStorage.removeItem('pending_activation_code')
            setResult(data)
            setStage('success')

        } catch (err) {
            setErrorMsg('Something went wrong. Please try again or contact support.')
            setStage('error')
        }
    }

    const { t } = useTranslation('university')

    const formatExpiry = (isoDate: string): string => {
        return new Date(isoDate).toLocaleDateString(undefined, {
            year: 'numeric', month: 'long', day: 'numeric'
        })
    }

    return (
        <div style={{
            background: M, minHeight: '100vh', display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: '24px', fontFamily: "'Inter', system-ui, sans-serif",
        }}>
            <SEO title="Activate" noindex />
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}
            >

                {/* Loading */}
                {stage === 'loading' && (
                    <div>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
                        <p style={{ color: MU }}>{t('activate.loading')}</p>
                    </div>
                )}

                {/* Needs Account */}
                {stage === 'needs_account' && (
                    <div>
                        <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2px', marginBottom: 16 }}>
                            {t('activate.eyebrow')}
                        </p>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>🎓</div>
                        <h1 style={{ fontSize: 26, color: TX, fontWeight: 900, margin: '0 0 12px', fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {t('activate.needsAccountTitle')}
                        </h1>
                        <p style={{ color: MU, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>
                            {t('activate.needsAccountSub')}
                        </p>
                        <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 12, padding: '14px 20px', marginBottom: 28 }}>
                            <p style={{ fontSize: 12, color: MU, margin: '0 0 4px' }}>{t('activate.codeLabel')}</p>
                            <p style={{ fontFamily: 'monospace', fontSize: 20, color: G, letterSpacing: '3px', margin: 0 }}>{cleanCode}</p>
                        </div>
                        <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
                            <motion.button
                                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                                onClick={() => navigate(`/signup?activate=${cleanCode}`)}
                                style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}
                            >
                                {t('activate.createAccountBtn')}
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                                onClick={() => navigate(`/signin?activate=${cleanCode}`)}
                                style={{ background: 'rgba(255,255,255,0.05)', color: TX, border: `1px solid ${BR}`, borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                            >
                                {t('activate.signInBtn')}
                            </motion.button>
                        </div>
                    </div>
                )}

                {/* Confirming */}
                {stage === 'confirming' && (
                    <div>
                        <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2px', marginBottom: 16 }}>
                            {t('activate.eyebrow')}
                        </p>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>🎓</div>
                        <h1 style={{ fontSize: 26, color: TX, fontWeight: 900, margin: '0 0 12px', fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {t('activate.confirmTitle')}
                        </h1>
                        <p style={{ color: MU, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>
                            {t('activate.confirmSub')}
                        </p>
                        <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 12, padding: '14px 20px', marginBottom: 28 }}>
                            <p style={{ fontSize: 12, color: MU, margin: '0 0 4px' }}>{t('activate.codeLabel')}</p>
                            <p style={{ fontFamily: 'monospace', fontSize: 20, color: G, letterSpacing: '3px', margin: 0 }}>{cleanCode}</p>
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                            onClick={activate}
                            style={{ width: '100%', background: `linear-gradient(135deg, ${EM}, #059669)`, color: '#fff', border: 'none', borderRadius: 12, padding: '16px', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 24px rgba(16,185,129,0.3)' }}
                        >
                            {t('activate.activateBtn')}
                        </motion.button>
                    </div>
                )}

                {/* Activating */}
                {stage === 'activating' && (
                    <div>
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
                            style={{ fontSize: 48, display: 'inline-block', marginBottom: 16 }}
                        >
                            ⏳
                        </motion.div>
                        <p style={{ color: MU, fontSize: 15 }}>{t('activate.activating')}</p>
                    </div>
                )}

                {/* Success */}
                {stage === 'success' && result && (
                    <div>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                            style={{ fontSize: 72, marginBottom: 16 }}
                        >
                            🎓
                        </motion.div>
                        <p style={{ fontSize: 11, color: EM, fontWeight: 800, letterSpacing: '2px', marginBottom: 12 }}>
                            {t('activate.successEyebrow')}
                        </p>
                        <h1 style={{ fontSize: 28, color: TX, fontWeight: 900, margin: '0 0 12px', fontFamily: "'Playfair Display', Georgia, serif" }}>
                            {t('activate.successTitle')}
                        </h1>
                        <p style={{ color: MU, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>
                            {t('activate.successSub', { months: result.duration_months })}
                            {result.expires_at && (
                                <> {t('activate.expiresAt', { date: formatExpiry(result.expires_at) })}</>
                            )}
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 28 }}>
                            {[
                                { n: '45', l: t('activate.statDegrees') },
                                { n: `${result.duration_months}`, l: t('activate.statMonths') },
                                { n: '∞', l: t('activate.statSessions') },
                            ].map(s => (
                                <div key={s.l} style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12, padding: '14px 8px' }}>
                                    <div style={{ fontSize: 24, fontWeight: 900, color: EM }}>{s.n}</div>
                                    <div style={{ fontSize: 11, color: MU }}>{s.l}</div>
                                </div>
                            ))}
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                            onClick={() => navigate('/university/dashboard')}
                            style={{ width: '100%', background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 12, padding: '16px', fontSize: 16, fontWeight: 800, cursor: 'pointer', marginBottom: 12 }}
                        >
                            {t('activate.dashboardBtn')}
                        </motion.button>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            onClick={() => navigate('/university/enrol')}
                            style={{ width: '100%', background: 'transparent', color: MU, border: `1px solid ${BR}`, borderRadius: 12, padding: '14px', fontSize: 14, cursor: 'pointer' }}
                        >
                            {t('activate.browseBtn')}
                        </motion.button>
                    </div>
                )}

                {/* Error */}
                {stage === 'error' && (
                    <div>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
                        <h1 style={{ fontSize: 24, color: TX, fontWeight: 900, margin: '0 0 12px' }}>
                            {t('activate.errorTitle')}
                        </h1>
                        <p style={{ color: MU, fontSize: 14, lineHeight: 1.7, margin: '0 0 24px' }}>
                            {errorMsg || t('activate.errorDefault')}
                        </p>
                        <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
                            <button
                                onClick={() => setStage('confirming')}
                                style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                            >
                                {t('activate.tryAgainBtn')}
                            </button>
                            <a
                                href="mailto:support@stillwatersacademy.com"
                                style={{ color: MU, fontSize: 13, textDecoration: 'none' }}
                            >
                                {t('activate.contactSupport')}
                            </a>
                        </div>
                    </div>
                )}

            </motion.div>
        </div>
    )
}
